import {User} from './user';
import {Community} from "./communities";

export interface FeedbackMessage {
    id: number;
    body: string;
    sender?: User | null;
    is_from_staff: boolean;
    created_at: string | Date;
}

export interface Feedback {
    responding?: boolean;
    response?: string | null;
    reviewed: boolean;
    id: number;
    user_detail?: User | null;
    is_anonymous: boolean;
    created_at: string | Date;
    updated_at: string | Date;
    deleted: boolean;
    message: string;
    community?: Community;
    messages?: FeedbackMessage[];
    replyDraft?: string;
    sendingReply?: boolean;
}

export function getFeedbackThread(feedback: Feedback): FeedbackMessage[] {
    const thread: FeedbackMessage[] = [];
    const apiMessages: FeedbackMessage[] = [...(feedback.messages ?? [])].sort((a: FeedbackMessage, b: FeedbackMessage): number =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );

    if (feedback.message) {
        thread.push({
            id: feedback.id * -1,
            body: feedback.message,
            sender: feedback.user_detail ?? null,
            is_from_staff: false,
            created_at: feedback.created_at,
        });
    }

    for (const message of apiMessages) {
        if (!thread.some((existingMessage: FeedbackMessage): boolean => sameFeedbackMessage(existingMessage, message))) {
            thread.push(message);
        }
    }

    if (feedback.response) {
        const legacyResponse: FeedbackMessage = {
            id: (feedback.id * -1) - 1,
            body: feedback.response,
            sender: null,
            is_from_staff: true,
            created_at: feedback.updated_at,
        };

        if (!thread.some((existingMessage: FeedbackMessage): boolean => sameFeedbackMessage(existingMessage, legacyResponse))) {
            thread.push(legacyResponse);
        }
    }

    return thread.sort((a: FeedbackMessage, b: FeedbackMessage): number =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
}

export function getFeedbackLastActivity(feedback: Feedback): string | Date {
    const thread: FeedbackMessage[] = getFeedbackThread(feedback);
    return thread.length ? thread[thread.length - 1].created_at : feedback.updated_at;
}

export function hasStaffReply(feedback: Feedback): boolean {
    return getFeedbackThread(feedback).some((message: FeedbackMessage): boolean => message.is_from_staff);
}

function sameFeedbackMessage(a: FeedbackMessage, b: FeedbackMessage): boolean {
    return a.body === b.body
        && a.is_from_staff === b.is_from_staff
        && getSenderId(a.sender) === getSenderId(b.sender)
        && new Date(a.created_at).getTime() === new Date(b.created_at).getTime();
}

function getSenderId(sender?: User | null): number | null {
    return sender?.id ?? null;
}
